import {
  environmentInfo,
  marketingInfo,
  passengersInfo,
  testDimensions,
  userInfo,
} from './datalayerMap';
import type {
  Datalayer,
  TMSAncillariesInfo,
  TMSEnvironmentInfo,
  TMSFlightInfo,
  TMSHotelInfo,
  TMSMarketingInfo,
  TMSPageInfo,
  TMSPassengersInfo,
  TMSPriceInfo,
  TMSProductInfo,
  TMSSearchInfo,
  TMSTestDimensions,
  TMSTransactionInfo,
  TMSUserInfo,
} from './types';
import { createEmptyObjectFromArray } from './utils';

const emptyEnvironment: Omit<TMSEnvironmentInfo, 'testDimensions'> &
  TMSTestDimensions = createEmptyObjectFromArray([
  ...environmentInfo,
  ...testDimensions,
]);

const emptyMarketing: TMSMarketingInfo = createEmptyObjectFromArray([
  ...marketingInfo,
]);

const emptyPassenger: TMSPassengersInfo = createEmptyObjectFromArray([
  ...passengersInfo,
]);

const emptyUser: TMSUserInfo = createEmptyObjectFromArray([...userInfo]);

const emptyAncillaries: TMSAncillariesInfo = {
  ancil_bag_assistance_price: '',
  ancil_bag_assistance: '',
  ancil_bag_price: '',
  ancil_bag_quantity: '',
  ancil_bag: '',
  ancil_cancellation_guarantee_price: '',
  ancil_cancellation_guarantee: '',
  ancil_confirmation_sms_price: '',
  ancil_confirmation_sms: '',
  ancil_esta_price: '',
  ancil_esta: '',
  ancil_evisa_price: '',
  ancil_evisa: '',
  ancil_flexible_date_price: '',
  ancil_flexible_date: '',
  ancil_insurance_price: '',
  ancil_insurance: '',
  ancil_post_ticket_price: '',
  ancil_post_ticket: '',
  ancil_seat_price: '',
  ancil_seat: '',
  ancil_service_pack_price: '',
  ancil_service_pack: '',
};

const emptyFlight: TMSFlightInfo = {
  flight_airline_code: '',
  flight_airline_name: '',
  flight_arrival_airport_code: '',
  flight_arrival_airport_name: '',
  flight_arrival_city_code: '',
  flight_arrival_city_name: '',
  flight_arrival_country_code: '',
  flight_arrival_country_name: '',
  flight_category: '',
  flight_class: '',
  flight_departure_airport_code: '',
  flight_departure_airport_name: '',
  flight_departure_city_code: '',
  flight_departure_city_name: '',
  flight_departure_country_code: '',
  flight_departure_country_name: '',
  flight_departure_date: '',
  flight_duration_trip: '',
  flight_duration: '',
  flight_fare_itinerary_type: '',
  flight_form: '',
  flight_low_CO2: '',
  flight_nSections: '',
  flight_nSegments: '',
  flight_return_date: '',
  flight_sections_airport_code: '',
  flight_sections_arrival_time: '',
  flight_sections_departure_time: '',
  flight_sections_flight_numbers: '',
  flight_segments_airport_code: '',
  flight_strategy_hub_smarthub_results: '',
  flight_strategy_hub_smarthub_selection: '',
  flight_type: '',
  in_fareItineraryKey: '',
  in_segmentKeys: '',
};

const emptyHotel: TMSHotelInfo = {
  hotel_check_in_date: '',
  hotel_check_out_date: '',
  hotel_city_name_search: '',
  hotel_city_name: '',
  hotel_country_code: '',
  hotel_country_name: '',
  hotel_name: '',
  hotel_nNights: 0,
  hotel_nRooms: 0,
  hotel_picture_URL: '',
  hotel_price: 0,
  hotel_product_id: '',
  hotel_room_name: '',
  hotel_room_price: 0,
  hotel_room_type: '',
  hotel_stars: '',
};

const emptyPage: TMSPageInfo = {
  env_template: '',
  page_category: '',
  page_name: '',
  page_previous: '',
  page_url: '',
};

const emptyPrice: TMSPriceInfo = {
  prime_added: '',
  prime_benefit: '',
  prime_discount_price: '',
  prime_free_trial: '',
  prime_price_selected: '',
  prime_price: '',
  prime_renewal_revenue: '',
  product_default_price: '',
  product_quantity: '',
  product_total_price_cheapest_payment_fee: '',
  product_total_price_dom: '',
  product_total_price_eur: '',
  product_total_price: '',
};

const emptyProduct: TMSProductInfo = {
  product: '',
  product_code: '',
  product_id: '',
  product_nAdults: '',
  product_nChildren: '',
  product_nInfants: '',
  product_nPax: '',
};

const emptySearch: TMSSearchInfo = {
  accommodationFunnel_search_id: '',
  flight_strategy_hub_smarthub_results: '',
  hotels_search_id_oc: '',
  hotels_search_id: '',
  in_searchID: '',
  search_direct_flight_checkbox: '',
  search_flight_recommended_airline_code: '',
  search_flight_recommended_airline_name: '',
  search_flight_recommended_price: '',
  search_hotel_recommended_price: '',
  search_recommended_hotel_name: '',
  search_recommended_hotel_picture_url: '',
  search_recommended_hotel_stars: '',
  search_top_results: '',
};

const emptyTransaction: TMSTransactionInfo = {
  g_conv_id: '',
  transaction_booking_status: '',
  transaction_id: '',
  transaction_payment_type: '',
  transaction_price_eur: '',
  transaction_price: '',
  transaction_r_forecast: '',
  transaction_r_marketing: '',
  transaction_r_sem: '',
  transaction_redemption_rate: '',
  transaction_shipping_cost: '',
  transaction_voucher_code: '',
};

/**
 * Creates an empty datalayer object with all properties set to empty strings or default values.
 * This is useful for initializing a datalayer before populating it with actual data.
 * @returns {Datalayer} An object representing an empty datalayer.
 */
export const emptyDatalayer: Datalayer = {
  ...emptyEnvironment,
  ...emptyMarketing,
  ...emptyPassenger,
  ...emptyUser,
  ...emptyAncillaries,
  ...emptyFlight,
  ...emptyHotel,
  ...emptyPage,
  ...emptyPrice,
  ...emptyProduct,
  ...emptySearch,
  ...emptyTransaction,
};
